import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { useSearchParams } from "react-router-dom";
import { AiOutlineClose } from "react-icons/ai";

import ModalLayout from "@/components/Layout/ModalLayout";
import { daysOfTheWeek } from "@/constants/calendar";
import ReservationCard from "@/pages/Home/components/ReservationCard";
import ReservationDetailModal from "@/pages/Home/components/ReservationDetailModal";
import VehicleReservationCard from "@/pages/Home/components/VehicleReservationCard";
import useCalendarStore from "@/store/calendarStore";
import { fetchVehicleReservationsByDate } from "@/apis/reservation";
import type { DailyReservation, DailyReservationData } from "@/types/reservation";
import type { VehicleReservation } from "@/types/vehicleReservation";
import { cn } from "@/utils/cn";

interface DailyReservationModalProps {
  date: Dayjs;
  onClose: () => void;
}

const DailyReservationModal = ({ date, onClose }: DailyReservationModalProps) => {
  const [searchParams] = useSearchParams();
  const activeTab = Number(searchParams.get("tab")) || 0;
  const isVehicleTab = activeTab === 1;

  const [monthlyReservations] = useCalendarStore(
    useShallow(state => [state.monthlyReservations]),
  );

  const [vehicleReservations, setVehicleReservations] = useState<VehicleReservation[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<DailyReservationData | null>(null);

  useEffect(() => {
    if (isVehicleTab) {
      const loadDaily = async () => {
        const rsvtDt = date.format("YYYYMMDD");
        const data = await fetchVehicleReservationsByDate(rsvtDt);
        setVehicleReservations(data);
      };
      loadDaily();
    }
  }, [date, isVehicleTab]);

  const dailyReservations = monthlyReservations?.filter(
    (reservation: DailyReservation) =>
      date.date() === dayjs(reservation.day).date() &&
      date.month() === dayjs(reservation.day).month(),
  )[0];

  const reservationList: DailyReservationData[] =
    dailyReservations?.data ?? [];

  const placeNames = useMemo(() => {
    const names = [...new Set(reservationList.map(r => r.plcNm))];
    return names.sort();
  }, [reservationList]);

  const destinations = useMemo(() => {
    const names = [...new Set(vehicleReservations.map(r => r.destination))];
    return names.sort();
  }, [vehicleReservations]);

  const showPlaceFilter = !isVehicleTab && placeNames.length > 1;
  const showDestFilter = isVehicleTab && destinations.length > 1;

  const filteredReservations = selectedPlace
    ? reservationList.filter(r => r.plcNm === selectedPlace)
    : reservationList;

  const filteredVehicleReservations = selectedPlace
    ? vehicleReservations.filter(r => r.destination === selectedPlace)
    : vehicleReservations;

  useEffect(() => {
    setSelectedPlace(null);
  }, [isVehicleTab]);

  const dateLabel = `${date.format("MM / DD")} (${daysOfTheWeek[date.day()]})`;

  const filterButtons = showPlaceFilter ? placeNames : showDestFilter ? destinations : [];

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex h-[520px] max-h-[80vh] min-w-0 w-[calc(100vw-32px)] flex-col sm:w-[460px]">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">
            {dateLabel} 예약 현황
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Filter */}
        {filterButtons.length > 0 && (
          <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-gray-100 px-6 py-3">
            <button
              type="button"
              className={cn(
                "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                selectedPlace === null
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200",
              )}
              onClick={() => setSelectedPlace(null)}
            >
              전체
            </button>
            {filterButtons.map(name => (
              <button
                key={name}
                type="button"
                className={cn(
                  "shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  selectedPlace === name
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200",
                )}
                onClick={() => setSelectedPlace(name)}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isVehicleTab ? (
            <VehicleReservationCard
              date={dateLabel}
              reservationList={filteredVehicleReservations}
              showHeader={false}
            />
          ) : (
            <ReservationCard
              date={dateLabel}
              reservationList={filteredReservations}
              showHeader={false}
              onItemClick={(item) => setDetailItem(item)}
            />
          )}
        </div>
      </div>

      {detailItem && (
        <ReservationDetailModal
          reservation={detailItem}
          onClose={() => setDetailItem(null)}
        />
      )}
    </ModalLayout>
  );
};

export default DailyReservationModal;
